import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    pointerWithin,
    rectIntersection,
    getFirstCollision,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import React, { FC, useEffect, useRef, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import createDashboardUpdateFetcher from '../../api/dashboard/updateDashoard';
import deleteFetcher from '../../api/deleteFetcher';
import updateIndicatorOrderFetcher from '../../api/indicator/updateIndicatorOrderFetcher';
import { DashboardModeProvider } from '../../contexts/dashboardModeContext';
import { useLanguage } from '../../contexts/languageContext';
import { TenantContext } from '../../contexts/tenantContext';
import useFetcher from '../../utils/hooks/useFetcher';
import { CHART_INDICATOR_NAME } from '../Modals/DashboardModal/constants';
import { ChatingProps } from '../Modals/DashboardModal/dashboardModalInterface';
import { FilterResults } from '../Modals/FilterModal/filterModalInterface';
import { SurveyTemporalFilterType } from '../Surveys/surveyInterface';
import ColumnDroppable from './columnDroppable';
import CompareToGlobalButton from './CompareToGlobal/compareToGlobalButton';
import DashboardHeader from './DashboardHeader/dashboardHeader';
import {
    Indicator,
    IndicatorData,
    IndicatorsOrder,
    DonutProps,
    VerbatimProps,
    DashboardFromApi,
    UpdateDashboardProps,
    FilterFromApi,
    ClauseFilterProps,
    ClauseFilterLabelProps,
    DashboardParams,
    DonutFromApiProps,
    KeyFigureFromApiProps,
    ScopeProps,
    ScopeLabelProps,
    TemporalScopeFromApi,
    CrosstableProps,
} from './dashboardInterfaces';
import FilterBar from './FilterBar/filterBar';
import ChatbotIndicator from './IndicatorCard/chatbotIndicator';
import CrosstableIndicator from './IndicatorCard/crosstableIndicator';
import DonutIndicator from './IndicatorCard/donutIndicator';
import { getEvolution } from './IndicatorCard/evolution';
import GlobalVerbatimIndicator from './IndicatorCard/globalVerbatimIndicator';
import HorizontalBarIndicator from './IndicatorCard/horizontalBarIndicator';
import KeyFigureIndicator from './IndicatorCard/keyFigureIndicator';
import LineChartIndicator from './IndicatorCard/lineChartIndicator';
import VerbatimIndicator from './IndicatorCard/verbatimIndicator';
import { ContainerDashboardCharts, DashboardStyled, SelectFiltersBar } from './styled';
import getDefaultDateRange from './TemporalScope/defaultDateRange';
import TemporalScope from './TemporalScope/temporalScope';

// Comparaisons (période et base ensemble) masquées pour la mise en production : passer à true pour les réactiver
const IS_COMPARISON_ENABLED = true;

function isKeyFigureProps(object: IndicatorData): object is KeyFigureFromApiProps {
    return 'data' in object;
}

function isDonutProps(object: IndicatorData): object is DonutFromApiProps {
    return 'donut_result' in object;
}

function isVerbatimProps(object: IndicatorData): object is VerbatimProps {
    return 'positive' in object;
}

function isCrosstableProps(object: IndicatorData): object is CrosstableProps {
    return 'columns' in object;
}

const Dashboard: FC = () => {
    const { t } = useTranslation('dashboard');
    const { mutate } = useSWRConfig();
    const { surveyId = 'unknown', dashboardId } = useParams<DashboardParams>();
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const [updatedDasboard, setUpdatedDasboard] = useState<UpdateDashboardProps>();
    const { selectedLanguage } = useLanguage();
    const selectedLanguageForSurvey = selectedLanguage && selectedLanguage.surveysLanguagesIds.find((surveyLanguage) => String(surveyLanguage.surveyId) === surveyId);
    const dashboardUrl = `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}`;
    const fetcher = useFetcher();
    const { data: fetchedDashboard, error } = useSWR((selectedLanguageForSurvey && !selectedLanguageForSurvey.isDefault) ? `${dashboardUrl}/?translate=${selectedLanguageForSurvey.languageId}` : dashboardUrl, fetcher);
    const { data: fetchedIndicators } = useSWR((selectedLanguageForSurvey && !selectedLanguageForSurvey.isDefault) ? `${dashboardUrl}/indicator/?translate=${selectedLanguageForSurvey.languageId}` : `${dashboardUrl}/indicator`, fetcher);
    const { data: fetchedSurvey } = useSWR(`${process.env.REACT_APP_API_URL}/survey/${surveyId}`, fetcher);
    const navigate = useNavigate();

    const [filters, setFilters] = useState<FilterFromApi[]>([]);
    const [temporalScopes, setTemporalScope] = useState<TemporalScopeFromApi[]>([]);
    const [selectedClauseFilter, setSelectedClauseFilter] = useState<Array<ClauseFilterProps>>([]);
    const [selectedClauseFilterLabel, setSelectedClauseFilterLabel] = useState<Array<ClauseFilterLabelProps>>([]);
    const [selectedScope, setSelectedScope] = useState<Array<ScopeProps>>([]);
    const [selectedScopeLabel, setSelectedScopeLabel] = useState<Array<ScopeLabelProps>>([]);
    const [resetFilters, setResetFilters] = useState<number>(0);
    const [isComparedToGlobal, setIsComparedToGlobal] = useState<boolean>(false);
    const [isComparedToPeriod, setIsComparedToPeriod] = useState<boolean>(false);
    const [comparedScope, setComparedScope] = useState<Array<ScopeProps>>([]);
    // The compared calendar opens empty, nothing is compared until it is filled,
    // and resetting brings the main one back to the same empty state.
    const emptyDateRange = () => ({ selection: { startDate: undefined, endDate: undefined, key: 'selection' } });
    const [comparedDateRange, setComparedDateRange] = useState<any>(emptyDateRange);
    const [dateRange, setDateRange] = useState<any>(emptyDateRange);
    const constructDashboardFilterConfigFromApiToUpdate = (dashboardFilterConfig: DashboardFromApi['dashboardFilterConfig']): UpdateDashboardProps['dashboardFilterConfig'] => (dashboardFilterConfig.map((dashboardFilter: FilterFromApi) => ({ filterId: Object.keys(dashboardFilter.filter)[0], modalitiesIds: Object.keys(dashboardFilter.modalities) })));
    const handleSelectClauseFilter = ([filterId, modalityId]: [string, Array<string> | null]): void => {
        const indexOf = selectedClauseFilter.findIndex((alreadySelected: ClauseFilterProps) => alreadySelected.filterId === filterId);

        if (modalityId !== null && modalityId?.length !== 0) {
            if (indexOf === -1) {
                setSelectedClauseFilter([...selectedClauseFilter, { filterId, modalityId }]);
            } else {
                const newSelectedClauseFilter = [...selectedClauseFilter];
                newSelectedClauseFilter[indexOf] = { filterId, modalityId };
                setSelectedClauseFilter(newSelectedClauseFilter);
            }
        } else {
            const newSelectedClauseFilter = [...selectedClauseFilter];
            newSelectedClauseFilter.splice(indexOf, 1);
            if (indexOf !== -1) {
                setSelectedClauseFilter(newSelectedClauseFilter);
            }
        }
    };

    const handleSelectClauseFilterLabel = ([filterLabel, modalitiesLabel]: [string, string | null]): void => {
        const indexOf = selectedClauseFilterLabel.findIndex((alreadySelected: ClauseFilterLabelProps) => alreadySelected.filterLabel === filterLabel);
        if (modalitiesLabel !== null && modalitiesLabel?.length !== 0) {
            if (indexOf === -1) {
                setSelectedClauseFilterLabel([...selectedClauseFilterLabel, { filterLabel, modalitiesLabel }]);
            } else {
                const newSelectedClauseFilterLabel = [...selectedClauseFilterLabel];
                newSelectedClauseFilterLabel[indexOf] = { filterLabel, modalitiesLabel };
                setSelectedClauseFilterLabel(newSelectedClauseFilterLabel);
            }
        } else {
            const newSelectedClauseFilterLabel = [...selectedClauseFilterLabel];
            newSelectedClauseFilterLabel.splice(indexOf, 1);
            if (indexOf !== -1) {
                setSelectedClauseFilterLabel(newSelectedClauseFilterLabel);
            }
        }
    };

    const handleSelectedScope = ([scopeId, modalityId]: [string, Array<string> | null]): void => {
        const indexOf = selectedScope.findIndex((alreadySelected: ScopeProps) => alreadySelected.scopeId === scopeId);
        if (modalityId !== null && modalityId?.length !== 0) {
            if (indexOf === -1) {
                setSelectedScope([{ scopeId, modalityId }]);
            } else {
                setSelectedScope([{ scopeId, modalityId }]);
            }
        } else {
            const newSelectedScope = [...selectedScope];
            newSelectedScope.splice(indexOf, 1);
            if (indexOf !== -1) {
                setSelectedScope(newSelectedScope);
            }
        }
    };

    const handleSelectedComparedScope = ([scopeId, modalityId]: [string, Array<string> | null]): void => {
        if (modalityId !== null && modalityId?.length !== 0) {
            setComparedScope([{ scopeId, modalityId }]);

            return;
        }
        setComparedScope(comparedScope.filter((alreadySelected: ScopeProps) => alreadySelected.scopeId !== scopeId));
    };

    // The endpoint takes a single comparison, so the two cannot both be active.
    const handleToggleCompareToGlobal = (isCompared: boolean): void => {
        setIsComparedToGlobal(isCompared);
        if (isCompared) {
            setIsComparedToPeriod(false);
        }
    };

    const handleToggleCompareToPeriod = (isCompared: boolean): void => {
        setIsComparedToPeriod(isCompared);
        if (isCompared) {
            setIsComparedToGlobal(false);
        } else {
            setComparedScope([]);
            setComparedDateRange(emptyDateRange());
        }
    };

    const handleResetTemporalScope = (): void => {
        setSelectedScope([]);
        setSelectedScopeLabel([]);
        setComparedScope([]);
        setComparedDateRange(emptyDateRange());
        setDateRange(emptyDateRange());
        setIsComparedToPeriod(false);
    };

    const handleChangeDateRangeScope = (newDateRange: any): void => {
        setDateRange(newDateRange);
    };

    const handleChangeComparedDateRangeScope = (newDateRange: any): void => {
        setComparedDateRange(newDateRange);
    };

    useEffect(() => {
        const combinedScopes: Array<ScopeLabelProps> = (!temporalScopes || selectedScope.length === 0) ? [] : selectedScope.map((selected: ScopeProps) => {
            // Find the corresponding temporal scope
            const temporalScope = temporalScopes.find((temporal: TemporalScopeFromApi) => temporal.scope[selected.scopeId as keyof typeof temporal.scope]);

            if (!temporalScope) {
                return undefined; // Handle case where scope is not found
            }

            // Get scope label
            const scopeLabel = temporalScope.scope[selected.scopeId as keyof typeof temporalScope.scope];

            // Get modalities labels for the selected modality IDs
            const modalitiesLabels = selected.modalityId.map((modalityId) => temporalScope.modalities[modalityId as keyof typeof temporalScope.modalities]).filter(Boolean); // Filter out undefined values

            return {
                scopeId: parseInt(selected.scopeId, 10),
                scopeLabel,
                modalitiesLabel: modalitiesLabels.join(', '), // Join multiple modalities with comma
            } as ScopeLabelProps;
        }).filter((scope): scope is ScopeLabelProps => scope !== undefined);
        setSelectedScopeLabel(combinedScopes);
    }, [selectedScope, temporalScopes]);

    const handleSelectedScopeLabel = ([scopeId, scopeLabel, modalitiesLabel]: [number, string, string | null]): void => {
        const indexOf = selectedScopeLabel.findIndex((alreadySelectedLabel: ScopeLabelProps) => alreadySelectedLabel.scopeId === scopeId);
        if (modalitiesLabel !== null) {
            setSelectedScopeLabel([{ scopeId, scopeLabel, modalitiesLabel }]);
        } else {
            const newSelectedScopeLabel = [...selectedScopeLabel];
            newSelectedScopeLabel.splice(indexOf, 1);
            if (indexOf !== -1) {
                setSelectedScopeLabel(newSelectedScopeLabel);
            }
        }
    };

    const handleResetClauseFilter = (hasResetFilters: number) => {
        setResetFilters(resetFilters + hasResetFilters);
    };

    useEffect(() => {
        if (fetchedDashboard?.dashboardFilterConfig.length > 0) {
            const selectedClauseLockedFilters = fetchedDashboard?.dashboardFilterConfig.filter((filter: FilterFromApi) => filter.lockedModalities)?.map((filter: any) => (
                { filterId: Object.keys(filter.filter)[0], modalityId: filter.lockedModalities.toString().split(',') }
            ));
            const selectedClauseLockedFiltersLabel = fetchedDashboard?.dashboardFilterConfig.filter((filter: FilterFromApi) => filter.lockedModalities)?.map((filter: any) => (
                { filterLabel: Object.values(filter.filter)[0], modalitiesLabel: Object.entries(filter.modalities).filter((modality) => String(filter.lockedModalities).includes(modality[0])).map((modality) => modality[1]).join(', ') }
            ));
            setSelectedClauseFilter(selectedClauseLockedFilters ?? []);
            setSelectedClauseFilterLabel(selectedClauseLockedFiltersLabel ?? []);
        } else {
            setSelectedClauseFilter([]);
            setSelectedClauseFilterLabel([]);
        }
    }, [fetchedDashboard, resetFilters]);

    useEffect(() => {
        if (error) {
            navigate('/');
        }
    }, [error]);

    useEffect(() => {
        if (fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE) {
            setDateRange(getDefaultDateRange(fetchedSurvey.defaultDatePeriod));
        }
    }, [fetchedSurvey?.surveyid, fetchedSurvey?.temporalFilterType, fetchedSurvey?.defaultDatePeriod]);

    const constructUpdateDashboard = (dashboard: DashboardFromApi): UpdateDashboardProps => (
        {
            id: dashboard.id,
            name: dashboard.name,
            surveyId: dashboard.surveyId,
            rankOfIndicator: dashboard.rankOfIndicator,
            slug: dashboard.slug,
            customSurveyTitle: dashboard.customSurveyTitle,
            dashboardFilterConfig: constructDashboardFilterConfigFromApiToUpdate(dashboard.dashboardFilterConfig),
        }
    );

    useEffect(() => {
        if (fetchedDashboard) {
            setUpdatedDasboard(constructUpdateDashboard(fetchedDashboard));
            setFilters(fetchedDashboard.dashboardFilterConfig);
            setTemporalScope(fetchedDashboard.temporalScopesConfig ? fetchedDashboard.temporalScopesConfig : []);
        }
    }, [fetchedDashboard]);

    const addDashboardFilter = (newFilters: FilterResults): void => {
        const newUpdatedDasboard: UpdateDashboardProps = { ...updatedDasboard, dashboardFilterConfig: newFilters };
        createDashboardUpdateFetcher(
            surveyId,
            newUpdatedDasboard,
            t('addFilter.modal.success'),
            t('addFilter.modal.error'),
            selectedSchema || '',
        );
        mutate(dashboardUrl);
    };

    const removeDashboardFilter = (filterId: string): void => {
        const urlToDeleteFilter = `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/filterConfig/${filterId}`;
        deleteFetcher(t('removeFilter.success'), t('removeFilter.error'), urlToDeleteFilter, selectedSchema || '');
        mutate(dashboardUrl);
        handleSelectClauseFilter([filterId, []]);
    };

    const selectIndicatorCard = (additionalVars: Indicator['additionalVars'], displayType: Indicator['displayType'], data: Indicator['data'], customTitle: Indicator['customItemTitle'], question: Indicator['question'], getChatbotResponse: (isSummary: boolean, question: string) => void, conversation: Array<ChatingProps>, summary: string, isSummaryDisplayed: boolean, waitingForChatAnswer: boolean, handleIsRenderedChat: (chatIdRendered: number) => void, surveyName: string | undefined, dasboardName: string | undefined) => {
        if (displayType === CHART_INDICATOR_NAME.CHATBOT) {
            return (
                <ChatbotIndicator
                    conversation={conversation}
                    dashboardName={dasboardName}
                    getChatbotResponse={getChatbotResponse}
                    handleIsRenderedChat={handleIsRenderedChat}
                    isSummaryDisplayed={isSummaryDisplayed}
                    question={question}
                    selectedClauseFilterLabel={selectedClauseFilterLabel}
                    selectedScopeLabel={selectedScopeLabel}
                    summary={summary}
                    surveyId={surveyId}
                    surveyName={surveyName}
                    waitingForChatAnswer={waitingForChatAnswer}
                />
            );
        }

        if (data === undefined) {
            return null;
        }

        if (displayType === CHART_INDICATOR_NAME.KEY_FIGURE && isKeyFigureProps(data)) {
            return (
                <KeyFigureIndicator
                    base={data.base}
                    comparedNumber={data.comparison ? Object.values(data.comparison.results.data.data)[0] : undefined}
                    comparisonType={data.comparison?.type}
                    computeMethod={Object.keys(data.data)[0]}
                    evolution={getEvolution(data.comparison?.type, data.comparison?.results?.data?.significance, Object.keys(data.data)[0])}
                    keyFigureNumber={Object.values(data.data)}
                    previousNumber={data?.previousNumber}
                    quantiles={data?.quantiles}
                    round={data?.round}
                    year={data?.year}
                />
            );
        }

        if (displayType === CHART_INDICATOR_NAME.DONUT && isDonutProps(data)) {
            const donutIndication: DonutProps = {
                keyFigure: data.donut_key_result ?? '',
                base: data.base,
                hasIndicatorKpiGroup: data.has_indicator_kpi_group,
                labels: Object.keys(data.donut_result),
                datasets: [{ label: 'label', data: Object.values(data.donut_result), backgroundColor: Object.values(data.donut_colors) }],
                round: data.round,
                comparisonType: data.comparison?.type,
                comparedKeyFigure: data.comparison?.results?.donut_key_result?.data,
                comparedValues: data.comparison?.results?.donut_result?.data,
                keyFigureEvolution: data.donut_key_result
                    ? getEvolution(data.comparison?.type, data.comparison?.results?.donut_key_result?.significance, Object.keys(data.donut_key_result)[0])
                    : null,
                valueEvolutions: Object.fromEntries(Object.keys(data.donut_result).map((label) => [
                    label,
                    getEvolution(data.comparison?.type, data.comparison?.results?.donut_result?.significance, label),
                ])),
            };

            return (
                <DonutIndicator
                    donutIndication={donutIndication}
                />
            );
        }
        if (displayType === CHART_INDICATOR_NAME.VERBATIM_PN && isVerbatimProps(data)) {
            return (
                <VerbatimIndicator negative={data.negative} positive={data.positive} />
            );
        }
        if (displayType === CHART_INDICATOR_NAME.VERBATIM) {
            return (
                <GlobalVerbatimIndicator question={question} selectedClauseFilterLabel={selectedClauseFilterLabel} verbatims={data} />
            );
        }
        if (displayType === CHART_INDICATOR_NAME.HORIZONTAL_BAR) {
            return (
                <HorizontalBarIndicator
                    listOfValues={data}
                />
            );
        }
        if (displayType === CHART_INDICATOR_NAME.LINE_CHART) {
            return (
                <LineChartIndicator
                    listOfValues={data}
                />
            );
        }
        if (displayType === CHART_INDICATOR_NAME.CROSS_TABLE && isCrosstableProps(data)) {
            return (
                <CrosstableIndicator crosstableResult={data} questionLabel={customTitle ?? (`${question.label} X ${data?.crossing_question_label}`)} selectedClauseFilterLabel={selectedClauseFilterLabel} />
            );
        }

        return undefined;
    };

    const columnsConfig: number[] = fetchedDashboard?.columnsConfig ?? [33, 33, 34];
    const columnsCount = columnsConfig.length;

    const [indicatorsByColumns, setIndicatorByColumns] = useState<{[key: number]: Array<Indicator>}>({});
    const [indicatorsOrder, setIndicatorsOrder] = useState<Array<IndicatorsOrder>>([]);
    const [isDragging, setIsDragging] = useState<string | null>(null);
    const recentlyMovedToNewContainer = useRef(false);

    useEffect(() => {
        if (fetchedIndicators) {
            const constructIndicatorsByColumns: {[key: number]: Array<Indicator>} = {};
            for (let col = 0; col < columnsCount; col++) {
                constructIndicatorsByColumns[col] = fetchedIndicators.filter((indicator: Indicator) => indicator.dashboardColumn === col);
            }

            setIndicatorByColumns({ ...constructIndicatorsByColumns });
            setIndicatorsOrder(fetchedIndicators.map((indicator: Indicator) => ({ id: indicator.id, dashboard_column: indicator.dashboardColumn, rank: indicator.rank })));
        }
    }, [fetchedIndicators, dashboardUrl, columnsCount]);

    const setIndicatorsNewOrder = () => {
        const prevIndicatorsOrder = [...indicatorsOrder];
        const newIndicatorOrders = Object.keys(indicatorsByColumns).flatMap((columnId: string) => (
            indicatorsByColumns[parseInt(columnId, 10)].map((indicator: Indicator, index) => ({ id: indicator.id, dashboard_column: parseInt(columnId, 10), rank: index }))
        ));
        if ((JSON.stringify(prevIndicatorsOrder) !== JSON.stringify(newIndicatorOrders))) {
            setIndicatorsOrder(newIndicatorOrders);
            updateIndicatorOrderFetcher(
                `${dashboardUrl}/indicator/update_order`,
                newIndicatorOrders,
                t('rankingIndicatorModal.success'),
                t('rankingIndicatorModal.error'),
                selectedSchema || '',
            );
        }
    };

    const findColumn = (unique: string | null) => {
        if (!unique) {
            return null;
        }
        if (Object.keys(indicatorsByColumns).some((c: string) => c === unique)) {
            return unique ? { id: unique, cards: indicatorsByColumns[parseInt(unique, 10)] } : null;
        }
        const id = String(unique);
        const itemWithColumnId = Object.keys(indicatorsByColumns).flatMap((c: string) => {
            const columnId = c;

            return indicatorsByColumns[parseInt(c, 10)].map((i: Indicator) => ({ itemId: i.id, columnId }));
        });
        const columnId = itemWithColumnId.find((i) => String(i.itemId) === id)?.columnId;

        return columnId ? { id: columnId, cards: indicatorsByColumns[parseInt(columnId, 10)] } : null;
    };

    const arraysAreEqual = (arr1: Array<Indicator>, arr2: Array<Indicator>): boolean => {
        if (arr1.length !== arr2.length) {
            return false;
        }

        return arr1.every((item, index) => item.id === arr2[index].id);
    };

    useEffect(() => {
        if (isDragging === '-1') {
            setIndicatorsNewOrder();
            mutate(`${dashboardUrl}/indicator`);
            setIsDragging(null);
        }
    }, [isDragging]);

    const handleDragStart = (event: DragStartEvent) => {
        recentlyMovedToNewContainer.current = false;
        setIsDragging(String(event.active.id));
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over, delta } = event;
        const activeId = String(active.id);
        const overId = over ? String(over.id) : null;
        const activeColumn = findColumn(activeId);
        const overColumn = findColumn(overId);
        if (!activeColumn || !overColumn || activeColumn.id === overColumn.id) {
            return null;
        }
        // Prevent oscillation: ignore cross-column moves immediately after a recent one.
        // The layout change from the previous move can cause a spurious opposite-column collision.
        if (recentlyMovedToNewContainer.current) {
            return null;
        }
        recentlyMovedToNewContainer.current = true;
        requestAnimationFrame(() => {
            recentlyMovedToNewContainer.current = false;
        });
        setIndicatorByColumns((prevState) => {
            const activeItems = activeColumn.cards;
            const overItems = overColumn.cards;
            const activeIndex = activeItems.findIndex((i: Indicator) => String(i.id) === activeId);
            const overIndex = overItems.findIndex((i: Indicator) => String(i.id) === overId);
            const newIndex = () => {
                const putOnBelowLastItem = overIndex === overItems.length - 1 && delta.y > 0;
                const modifier = putOnBelowLastItem ? 1 : 0;

                return overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            };
            const newIndicatorsByColumns: {[key: number]: Array<Indicator>} = { ...prevState };
            let hasChanges = false;
            for (let col = 0; col < columnsCount; col++) {
                if (String(col) === activeColumn.id) {
                    const newColumn = activeItems.filter((i) => String(i.id) !== activeId);
                    if (!arraysAreEqual(newColumn, prevState[col])) {
                        newIndicatorsByColumns[col] = newColumn;
                        hasChanges = true;
                    }
                } else if (String(col) === overColumn.id) {
                    const newColumn = [
                        ...overItems.slice(0, newIndex()),
                        activeItems[activeIndex],
                        ...overItems.slice(newIndex(), overItems.length),
                    ];
                    if (!arraysAreEqual(newColumn, prevState[col])) {
                        newIndicatorsByColumns[col] = newColumn;
                        hasChanges = true;
                    }
                } else {
                    newIndicatorsByColumns[col] = prevState[col];
                }
            }

            return hasChanges ? newIndicatorsByColumns : prevState;
        });

        return undefined;
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setIsDragging('-1');
        const activeId = String(active.id);
        const overId = over ? String(over.id) : null;
        const activeColumn = findColumn(activeId);
        const overColumn = findColumn(overId);
        if (!activeColumn || !overColumn || activeColumn.id !== overColumn.id) {
            return null;
        }
        const activeIndex = activeColumn.cards.findIndex((i) => String(i.id) === activeId);
        const overIndex = overColumn.cards.findIndex((i) => String(i.id) === overId);
        if (activeIndex !== overIndex) {
            setIndicatorByColumns((prevState) => {
                const newIndicatorsByColumns = { ...prevState };
                const columnIndex = parseInt(activeColumn.id, 10);
                newIndicatorsByColumns[columnIndex] = arrayMove(
                    overColumn.cards,
                    activeIndex,
                    overIndex,
                );

                return newIndicatorsByColumns;
            });
        }

        return undefined;
    };

    const collisionDetection = (args: Parameters<typeof pointerWithin>[0]) => {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) {
            // When multiple droppables are under the pointer (boundary between two columns),
            // prefer the one in the same column as the dragged card to prevent infinite oscillation.
            if (pointerCollisions.length > 1) {
                const activeColumn = findColumn(String(args.active.id));
                if (activeColumn) {
                    const sameColumnCollision = pointerCollisions.find((collision) => {
                        const col = findColumn(String(collision.id));

                        return col && col.id === activeColumn.id;
                    });
                    if (sameColumnCollision) {
                        return [sameColumnCollision];
                    }
                }
            }

            return pointerCollisions;
        }

        const firstCollision = getFirstCollision(rectIntersection(args));

        return firstCollision ? [firstCollision] : [];
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    return (
        <DashboardModeProvider>
            <DashboardStyled>
                <DashboardHeader name={fetchedSurvey?.customSurveyTitle || fetchedSurvey?.name} selectedClauseFilter={selectedClauseFilter} selectedDateRange={(fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE && dateRange.selection.startDate && dateRange.selection.endDate) ? dateRange.selection : undefined} selectedScope={selectedScope} surveyId={surveyId} />
                <SelectFiltersBar>
                    <TemporalScope
                        comparedDateRange={comparedDateRange}
                        comparedScope={comparedScope}
                        dateRange={dateRange}
                        handleChangeComparedDateRangeScope={handleChangeComparedDateRangeScope}
                        handleChangeDateRangeScope={handleChangeDateRangeScope}
                        handleResetTemporalScope={handleResetTemporalScope}
                        handleSelectedComparedScope={handleSelectedComparedScope}
                        handleSelectedScope={handleSelectedScope}
                        handleSelectedScopeLabel={handleSelectedScopeLabel}
                        handleToggleCompareToPeriod={IS_COMPARISON_ENABLED ? handleToggleCompareToPeriod : undefined}
                        isComparedToGlobal={isComparedToGlobal}
                        isComparedToPeriod={isComparedToPeriod}
                        isForHotAlert={false}
                        maxDate={new Date()}
                        minDate={new Date(fetchedSurvey?.minDate)}
                        selectedScope={selectedScope}
                        selectedScopeLabel={selectedScopeLabel}
                        temporalFilterType={fetchedSurvey?.temporalFilterType}
                        temporalScopes={temporalScopes}
                    />
                    <FilterBar
                        alreadySelectedFilter={updatedDasboard?.dashboardFilterConfig}
                        filters={filters}
                        handleResetClauseFilter={handleResetClauseFilter}
                        handleSelectClauseFilter={handleSelectClauseFilter}
                        handleSelectClauseFilterLabel={handleSelectClauseFilterLabel}
                        removeDashboardFilter={removeDashboardFilter}
                        resetFilters={resetFilters}
                        selectedClauseFilterLabel={selectedClauseFilterLabel}
                        setUpdatedDasboard={setUpdatedDasboard}
                        updateDashboard={addDashboardFilter}
                    />
                    {IS_COMPARISON_ENABLED && (
                        <CompareToGlobalButton
                            handleToggleCompareToGlobal={handleToggleCompareToGlobal}
                            isComparedToGlobal={isComparedToGlobal}
                            isComparedToPeriod={isComparedToPeriod}
                        />
                    )}
                </SelectFiltersBar>
                <ContainerDashboardCharts>
                    <DndContext
                        collisionDetection={collisionDetection}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragStart={handleDragStart}
                        sensors={sensors}
                    >
                        {Object.keys(indicatorsByColumns).map((columnNumber: string) => (
                            <ColumnDroppable
                                key={columnNumber}
                                columnNumber={columnNumber}
                                columnWidth={columnsConfig[parseInt(columnNumber, 10)]}
                                comparedDateRange={(isComparedToPeriod && fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE && comparedDateRange.selection.startDate && comparedDateRange.selection.endDate) ? comparedDateRange.selection : undefined}
                                comparedScope={isComparedToPeriod ? comparedScope : []}
                                dashboardName={fetchedDashboard?.name}
                                indicatorsByColumns={indicatorsByColumns}
                                isComparedToGlobal={isComparedToGlobal}
                                isDragging={isDragging}
                                selectIndicatorCard={selectIndicatorCard}
                                selectedClauseFilter={selectedClauseFilter}
                                selectedDateRange={(fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE && dateRange.selection.startDate && dateRange.selection.endDate) ? dateRange.selection : undefined}
                                selectedScope={selectedScope}
                                surveyName={fetchedSurvey?.name}
                            />
                        ))}
                    </DndContext>
                </ContainerDashboardCharts>
            </DashboardStyled>
        </DashboardModeProvider>
    );
};

export default Dashboard;
